import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import {
  InformationBox,
  OverlayLoader,
  SquareButton,
  useToast,
} from '@app/core';
import {
  Message,
  Participation,
  useCreateParticipationMessage,
  useParticipationMessages,
} from '@app/api';
import { MessageDetail } from './MessageDetail';
import { TextArea } from '@app/form';

const initialValues = {
  content: '',
};

interface FormValues {
  content: string;
}

const validationSchema = Yup.object().shape({
  content: Yup.string(),
});

const useStyles = createUseStyles((theme: Theme) => ({
  window: {
    paddingLeft: theme.marginBase * 4,
    paddingRight: theme.marginBase * 4,
    paddingTop: theme.marginBase,
    paddingBottom: theme.marginBase,
    flex: 1,
    overflow: 'auto',
  },
  send: {
    width: '100%',
    display: 'flex',
    alignItems: 'baseline',
    backgroundColor: theme.colors.white,
    boxShadow: theme.boxShadow.base,
    padding: theme.marginBase * 2,
  },
  button: {
    marginLeft: theme.marginBase,
  },
}));

interface Props {
  participation: Participation;
  participantView?: boolean;
  informationBox?: ReactNode;
}

export const MessagingTab = ({
  participation,
  participantView,
  informationBox,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();

  const refContainer = useRef<HTMLDivElement | null>(null);
  const participationId = participation.id;

  const { data: messages, isLoading } =
    useParticipationMessages(participationId);

  const { mutateAsync: createMessage } =
    useCreateParticipationMessage(participationId);

  const orderedMessages = useMemo(() => {
    if (!messages) {
      return [];
    }
    return messages.sort((a, b) => moment(a.sendAt).diff(b.sendAt));
  }, [messages]);

  useEffect(() => {
    refContainer.current &&
      refContainer.current.scrollTo(0, refContainer.current.scrollHeight);
  }, [messages]);

  const submit = useCallback(
    async (values: FormValues, helpers: FormikHelpers<any>) => {
      if (!values.content) {
        return;
      }
      try {
        await createMessage({ ...values, participationId });

        toast.saved();
        return helpers.resetForm();
      } catch (e) {
        helpers.setStatus(e);
        toast.exception(e);
      }
      helpers.setSubmitting(false);
    },
    [toast, createMessage, participationId],
  );

  if (isLoading && !messages) {
    return <OverlayLoader />;
  }

  return (
    <>
      <div className={classes.window} ref={refContainer}>
        {informationBox && <InformationBox text={informationBox} />}
        {orderedMessages &&
          orderedMessages.map((message: Message, index: number) => (
            <MessageDetail
              message={message}
              showDate={
                (index >= 1 &&
                  moment(orderedMessages[index - 1].sendAt).diff(
                    orderedMessages[index].sendAt,
                    'hours',
                  ) < -3) ||
                index === 0
              }
              key={message.id}
              participantView={participantView}
            />
          ))}
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={submit}
      >
        <Form>
          <div className={classes.send}>
            <TextArea name="content" noMargin />
            <SquareButton
              type="submit"
              color="blue"
              icon="ri-send-plane-2-line"
              className={classes.button}
            />
          </div>
        </Form>
      </Formik>
    </>
  );
};
